<template>
  <ManagerLayout>
    <div class="manager-dashboard">
      <h2 class="page-title">管理者ダッシュボード</h2>
      <p class="dashboard-links">
        <router-link to="/manager/instructors" class="link">📋 講師管理</router-link>
        <router-link to="/manager/submissions" class="link">📤 提出管理</router-link>
        <router-link to="/manager/feedback-playbacks" class="link">🔊 FB再生履歴</router-link>
        <router-link to="/manager/users" class="link">👥 ユーザー一覧</router-link>
        <router-link to="/manager/processing" class="link">⚙️ 処理・キュー</router-link>
        <router-link to="/manager/billing" class="link">💳 サブスク・支払い</router-link>
        <router-link to="/manager/learners" class="link">📚 学習者一覧</router-link>
        <router-link to="/manager/canceled-learners" class="link">📭 解約ユーザー</router-link>
      </p>

      <!-- よく使うリンク -->
      <section class="manager-section quick-links-section">
        <h3 class="section-title">よく使うリンク</h3>
        <div class="card quick-links-card">
          <ul class="quick-links-list">
            <li>
              <a :href="links.stripeDashboard" target="_blank" rel="noopener noreferrer" class="quick-link">
                💳 Stripe Dashboard
              </a>
            </li>
            <li>
              <a :href="links.supabase" target="_blank" rel="noopener noreferrer" class="quick-link">
                🗄️ Supabase Dashboard
              </a>
            </li>
            <li>
              <a :href="links.appUrl" target="_blank" rel="noopener noreferrer" class="quick-link">
                🌐 本番アプリ（{{ appUrlLabel }}）
              </a>
            </li>
          </ul>
        </div>
      </section>

      <!-- 休日・組織休日 -->
      <section class="manager-section holidays-section">
        <h3 class="section-title">休日・組織休日</h3>
        <div class="card holidays-card">
          <p class="holidays-desc">公式休日カレンダー（Admin）の参照・編集は以下のページから行えます。組織休日は Supabase の <code>organization_holidays</code> テーブルで管理されています。</p>
          <p class="holidays-link-row">
            <router-link to="/admin/holidays" class="btn btn-secondary">📅 公式休日カレンダー（Admin）を開く</router-link>
          </p>
        </div>
      </section>

      <!-- サマリ数字 -->
      <section class="manager-section summary-section">
        <h3 class="section-title">サマリ</h3>
        <div v-if="summaryLoading" class="summary-loading">読み込み中...</div>
        <div v-else class="summary-cards">
          <div class="summary-card">
            <span class="summary-label">登録ユーザー（保護者）</span>
            <span class="summary-value">{{ summary.usersCount }}</span>
            <span class="summary-unit">人</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">講師</span>
            <span class="summary-value">{{ summary.instructorsCount }}</span>
            <span class="summary-unit">人</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">有料サブスク</span>
            <span class="summary-value">{{ summary.activeSubscriptionsCount }}</span>
            <span class="summary-unit">件</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">アプリ内無料体験中（子）</span>
            <span class="summary-value">{{ summary.appTrialLearnersCount }}</span>
            <span class="summary-unit">人</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">今月の提出数</span>
            <span class="summary-value">{{ summary.submissionsThisMonth }}</span>
            <span class="summary-unit">件</span>
            <span class="summary-meta">{{ summary.monthLabel }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">今月のFB完了数</span>
            <span class="summary-value">{{ summary.feedbackCompletedThisMonth }}</span>
            <span class="summary-unit">件</span>
            <span class="summary-meta">{{ summary.monthLabel }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">bypass 付与中</span>
            <span class="summary-value">{{ summary.bypassCount }}</span>
            <span class="summary-unit">人</span>
          </div>
        </div>
        <div v-if="!summaryLoading && summary.monthLabel" class="summary-note">{{ summary.monthLabel }} の提出・FB完了を表示</div>
      </section>

      <!-- テスト用アカウント発行（bypass） -->
      <section class="manager-section">
        <h3 class="section-title">🆓 テスト用アカウント発行（bypass）</h3>
        <p class="section-desc">bypass を付与したユーザーはサブスク未登録でも学習画面を利用できます。</p>

        <!-- 既存ユーザーに bypass 付与 -->
        <div class="card">
          <h4>既存ユーザーに bypass を付与</h4>
          <div class="form-row">
            <label class="visually-hidden" for="manager-grant-email">付与するメールアドレス</label>
            <input
              id="manager-grant-email"
              v-model="grantEmail"
              name="grantEmail"
              type="email"
              placeholder="メールアドレス"
              class="form-input"
              autocomplete="email"
            />
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!grantEmail.trim() || grantLoading"
              @click="grantBypass"
            >
              {{ grantLoading ? '処理中...' : '付与する' }}
            </button>
          </div>
          <p v-if="grantMessage" class="form-message" :class="{ error: grantError }">{{ grantMessage }}</p>
        </div>

        <!-- 新規テストアカウント作成 -->
        <div class="card">
          <h4>新規テストアカウント作成</h4>
          <form class="create-test-form" @submit.prevent="createTestAccount">
            <div class="form-group">
              <label for="manager-create-email">メールアドレス</label>
              <input
                id="manager-create-email"
                v-model="createEmail"
                name="createEmail"
                type="email"
                placeholder="test@example.com"
                class="form-input"
                autocomplete="email"
              />
            </div>
            <div class="form-group">
              <label for="manager-create-password">パスワード（任意・未入力ならランダム）</label>
              <input
                id="manager-create-password"
                v-model="createPassword"
                name="createPassword"
                type="password"
                placeholder="未入力可"
                class="form-input"
                autocomplete="new-password"
              />
            </div>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="!createEmail.trim() || createLoading"
            >
              {{ createLoading ? '作成中...' : '作成する' }}
            </button>
          </form>
          <p v-if="createMessage" class="form-message" :class="{ error: createError }">{{ createMessage }}</p>
        </div>

        <!-- bypass 一覧 -->
        <div class="card">
          <h4>bypass 付与中のユーザー一覧</h4>
          <button type="button" class="btn btn-secondary btn-sm" :disabled="listLoading" @click="loadBypassList">
            {{ listLoading ? '読み込み中...' : '更新' }}
          </button>
          <div v-if="bypassList.length === 0 && !listLoading" class="empty-state">
            該当ユーザーはいません
          </div>
          <ul v-else class="bypass-list">
            <li v-for="u in bypassList" :key="u.id" class="bypass-item">
              <span class="bypass-email">{{ u.email }}</span>
              <span v-if="u.full_name" class="bypass-name">{{ u.full_name }}</span>
              <button
                type="button"
                class="btn btn-danger btn-sm"
                :disabled="revokeLoading === u.id"
                @click="revokeBypass(u)"
              >
                {{ revokeLoading === u.id ? '処理中...' : '解除' }}
              </button>
            </li>
          </ul>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''

const appUrl = import.meta.env.VITE_APP_URL || 'https://app.otoeigo.com'
const appUrlLabel = computed(() => {
  try {
    const u = new URL(appUrl)
    return u.hostname || appUrl
  } catch {
    return appUrl
  }
})
const links = reactive({
  stripeDashboard: import.meta.env.VITE_STRIPE_DASHBOARD_URL || 'https://dashboard.stripe.com',
  supabase: import.meta.env.VITE_SUPABASE_DASHBOARD_URL || 'https://supabase.com/dashboard',
  appUrl
})

const summaryLoading = ref(false)
const summary = reactive({
  usersCount: 0,
  instructorsCount: 0,
  activeSubscriptionsCount: 0,
  appTrialLearnersCount: 0,
  submissionsThisMonth: 0,
  feedbackCompletedThisMonth: 0,
  bypassCount: 0,
  monthLabel: ''
})

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

const loadSummary = async () => {
  summaryLoading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/dashboard-summary`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) return
    summary.usersCount = data.usersCount ?? 0
    summary.instructorsCount = data.instructorsCount ?? 0
    summary.activeSubscriptionsCount = data.activeSubscriptionsCount ?? 0
    summary.appTrialLearnersCount = data.appTrialLearnersCount ?? 0
    summary.submissionsThisMonth = data.submissionsThisMonth ?? 0
    summary.feedbackCompletedThisMonth = data.feedbackCompletedThisMonth ?? 0
    summary.bypassCount = data.bypassCount ?? 0
    summary.monthLabel = data.monthLabel ?? ''
  } catch (_) {
    // ignore
  } finally {
    summaryLoading.value = false
  }
}

const grantEmail = ref('')
const grantLoading = ref(false)
const grantMessage = ref('')
const grantError = ref(false)

const createEmail = ref('')
const createPassword = ref('')
const createLoading = ref(false)
const createMessage = ref('')
const createError = ref(false)

const bypassList = ref([])
const listLoading = ref(false)
const revokeLoading = ref(null)

const grantBypass = async () => {
  const email = grantEmail.value.trim()
  if (!email) return
  grantLoading.value = true
  grantMessage.value = ''
  grantError.value = false
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/grant-bypass`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      grantError.value = true
      grantMessage.value = data.error || '付与に失敗しました'
      return
    }
    grantMessage.value = `${email} に bypass を付与しました。`
    grantEmail.value = ''
    loadBypassList()
  } catch (e) {
    grantError.value = true
    grantMessage.value = e.message || 'エラーが発生しました'
  } finally {
    grantLoading.value = false
  }
}

const createTestAccount = async () => {
  const email = createEmail.value.trim()
  if (!email) return
  createLoading.value = true
  createMessage.value = ''
  createError.value = false
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/create-test-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        email,
        password: createPassword.value || undefined
      })
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      createError.value = true
      createMessage.value = data.error || '作成に失敗しました'
      return
    }
    createMessage.value = data.message || `${email} のテストアカウントを作成しました。`
    createEmail.value = ''
    createPassword.value = ''
    loadBypassList()
  } catch (e) {
    createError.value = true
    createMessage.value = e.message || 'エラーが発生しました'
  } finally {
    createLoading.value = false
  }
}

const loadBypassList = async () => {
  listLoading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/bypass-users`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      bypassList.value = []
      return
    }
    bypassList.value = data.users || []
  } catch (_) {
    bypassList.value = []
  } finally {
    listLoading.value = false
  }
}

const revokeBypass = async (u) => {
  revokeLoading.value = u.id
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/revoke-bypass`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ email: u.email })
    })
    if (res.ok) {
      loadBypassList()
    } else {
      const data = await res.json().catch(() => ({}))
      alert(data.error || '解除に失敗しました')
    }
  } catch (e) {
    alert(e.message || 'エラーが発生しました')
  } finally {
    revokeLoading.value = null
  }
}

onMounted(() => {
  loadSummary()
  loadBypassList()
})
</script>

<style scoped>
.manager-dashboard {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.dashboard-links {
  margin: 0 0 1.5rem 0;
}

.dashboard-links .link {
  color: #2563eb;
  text-decoration: none;
}

.dashboard-links .link:hover {
  text-decoration: underline;
}

.quick-links-section,
.holidays-section {
  margin-bottom: 2rem;
}

.quick-links-card {
  padding: 1rem 1.25rem;
}

.quick-links-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
}

.quick-link {
  color: #2563eb;
  text-decoration: none;
  font-size: 1rem;
}

.quick-link:hover {
  text-decoration: underline;
}

.holidays-card {
  padding: 1.25rem;
}

.holidays-desc {
  margin: 0 0 1rem 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.5;
}

.holidays-desc code {
  background: #f1f5f9;
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  font-size: 0.85em;
}

.holidays-link-row {
  margin: 0;
}

.holidays-link-row .btn {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  background: #e2e8f0;
  color: #334155;
  text-decoration: none;
  font-size: 0.9rem;
  border: 1px solid #cbd5e1;
}

.holidays-link-row .btn:hover {
  background: #cbd5e1;
}

.summary-section {
  margin-bottom: 2rem;
}

.summary-loading {
  padding: 1.5rem;
  text-align: center;
  color: #64748b;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 1rem;
}

.summary-card {
  background: white;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.summary-card .summary-label {
  font-size: 0.8rem;
  color: #64748b;
}

.summary-card .summary-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
}

.summary-card .summary-unit {
  font-size: 0.85rem;
  color: #475569;
}

.summary-card .summary-meta {
  font-size: 0.75rem;
  color: #94a3b8;
}

.summary-note {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #94a3b8;
}

.manager-section {
  margin-bottom: 2rem;
}

.section-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  color: #334155;
}

.section-desc {
  margin: 0 0 1rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.card h4 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: #475569;
}

.form-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
  color: #64748b;
}

.form-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9rem;
  min-width: 200px;
}

.form-message {
  margin: 0.75rem 0 0 0;
  font-size: 0.875rem;
  color: #059669;
}

.form-message.error {
  color: #dc2626;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-sm {
  padding: 0.35rem 0.75rem;
  font-size: 0.875rem;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-secondary {
  background: #e2e8f0;
  color: #475569;
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.empty-state {
  color: #94a3b8;
  font-size: 0.9rem;
  padding: 1rem 0;
}

.bypass-list {
  list-style: none;
  margin: 1rem 0 0 0;
  padding: 0;
}

.bypass-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
}

.bypass-item:last-child {
  border-bottom: none;
}

.bypass-email {
  font-weight: 500;
  min-width: 200px;
}

.bypass-name {
  color: #64748b;
  font-size: 0.9rem;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    align-items: stretch;
  }
  .form-input {
    min-width: 0;
  }
}
</style>
